import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import { featuredProjects } from '../data/portfolio'
import {
  EMPTY_VIEWPORT_HUD_STATS,
  emitHubTool,
  emitRigOverlay,
  emitJointOverlay,
  readShowRigOverlay,
  readShowJointOverlay,
  emitViewportShading,
  HUB_TOOL_EVENT,
  HUB_TOOL_IDS,
  OWNER_WINDOW_TOOL_IDS,
  startNewHubScene,
  RIG_OVERLAY_EVENT,
  JOINT_OVERLAY_EVENT,
  VIEWPORT_HUD_STATS_EVENT,
  VIEWPORT_LIGHTING,
  VIEWPORT_SHADING_BASE,
  VIEWPORT_SHADING_EVENT,
  readDialogueEnabled,
  setDialogueEnabled,
  DIALOGUE_TOGGLE_EVENT,
} from '../lib/hubTools'
import { ASSET_IDS } from '../lib/sceneLoadTracker'
import {
  loadingAttrs,
  useAssetReady,
  useSceneWantedAssets,
  useSceneWantedReady,
} from '../lib/hooks/useSceneReadiness'
import { CHARACTER_CLIPS } from '../data/characterClips'
import { getProjectDetailsPath, getProjectPath } from '../lib/utils/projects'
import { IS_OWNER } from '../lib/ownerMode'
import { requestNavigation } from '../lib/transitionCoordinator'

const MESH_TO_RIG_ASSET = {
  [ASSET_IDS.sakiraMesh]: ASSET_IDS.sakiraRig,
  [ASSET_IDS.songMesh]: ASSET_IDS.songRig,
}

const SCARED_ASSET_IDS = [
  CHARACTER_CLIPS.sakira.scared.assetId,
  CHARACTER_CLIPS.song.scared.assetId,
]

const sceneNameFromPath = (pathname) => {
  if (pathname === '/contact') return 'contact'
  if (pathname === '/about') return 'about'
  if (pathname.startsWith('/projects/')) {
    const id = pathname.split('/')[2]
    const project = featuredProjects.find((item) => item.id === id)
    return project?.title?.toLowerCase() || 'project'
  }
  if (pathname.startsWith('/projects')) return 'projects'
  return 'showreel'
}

const MayaLogo = () => (
  <img
    src="/images/maya/darrenico.png"
    alt=""
    className="maya-logo h-4 w-4 object-contain"
    aria-hidden="true"
  />
)

const HomeIcon = () => (
  <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden="true">
    <path
      fill="currentColor"
      d="M8 2.2 1.8 7.4h1.5V13h3.3V9.4h2.8V13h3.3V7.4h1.5L8 2.2z"
    />
  </svg>
)

const item = (label, action, extra = {}) => ({ label, action, ...extra })
const sep = { separator: true }

const MayaStatusIcon = ({ src }) => (
  <span
    className="maya-status-icon"
    style={{ '--maya-status-icon-src': `url(${process.env.PUBLIC_URL}${src})` }}
    aria-hidden="true"
  />
)

const MenuDot = ({ filled = false, reserved = false }) => (
  <span className="maya-menu-dot-slot" aria-hidden="true">
    <span
      className={
        reserved
          ? 'maya-menu-dot is-reserved'
          : filled
            ? 'maya-menu-dot is-filled'
            : 'maya-menu-dot'
      }
    />
  </span>
)

const DecorControl = ({ className, label, title, children }) => (
  <span
    className={className}
    aria-label={label}
    title={title || label}
    aria-disabled="true"
  >
    {children}
  </span>
)

const MayaQuickStatus = ({
  backEnabled,
  onNewScene,
  onBack,
  variant,
}) => {
  const isFlat = variant === 'flat'
  return (
    <>
      <button
        type="button"
        className="maya-status-btn"
        aria-label="New Scene"
        title="New Scene"
        disabled={isFlat}
        onClick={isFlat ? undefined : onNewScene}
      >
        <MayaStatusIcon src="/images/maya/new-file.png" />
      </button>
      <DecorControl className="maya-status-btn" label="Open Scene">
        <MayaStatusIcon src="/images/maya/open-file.png" />
      </DecorControl>
      <button
        type="button"
        className="maya-status-btn"
        aria-label="Save Tool Settings"
        title="Save Tool Settings"
        disabled={isFlat}
        onClick={isFlat ? undefined : () => emitHubTool('all', 'save')}
      >
        <MayaStatusIcon src="/images/maya/save-file.png" />
      </button>
      <button
        type="button"
        className="maya-status-btn"
        aria-label="Back"
        title="Back"
        disabled={!backEnabled}
        onClick={onBack}
      >
        <MayaStatusIcon src="/images/maya/undo.png" />
      </button>
      <DecorControl className="maya-status-btn" label="Forward" title="Redo">
        <MayaStatusIcon src="/images/maya/redo.png" />
      </DecorControl>
    </>
  )
}

const MayaViewportIcon = ({ src, className = '' }) => (
  <img
    src={src}
    alt=""
    className={['maya-viewport-icon', className].filter(Boolean).join(' ')}
    aria-hidden="true"
  />
)

const VIEWPORT_BASE_TOOLS = [
  {
    id: VIEWPORT_SHADING_BASE.smooth,
    label: 'Greyscale',
    title: 'Greyscale',
    src: '/images/maya/greyscale.png',
  },
  {
    id: VIEWPORT_SHADING_BASE.textured,
    label: 'Textured',
    title: 'Textured',
    src: '/images/maya/textured.png',
  },
]

const VIEWPORT_MENU_IDS = {
  view: 'view',
  shading: 'shading',
  lighting: 'lighting',
  show: 'show',
  renderer: 'renderer',
  panels: 'panels',
}

const WINDOW_TOOLS = [
  { id: HUB_TOOL_IDS.lights, label: 'Lighting Tool' },
  { id: HUB_TOOL_IDS.animation, label: 'Animation' },
  { id: HUB_TOOL_IDS.charactersDuoIdle, label: 'Characters — Duo Idle' },
  { id: HUB_TOOL_IDS.charactersDuoDance, label: 'Characters — Duo Dance' },
  { id: HUB_TOOL_IDS.charactersContactIdle, label: 'Characters — Contact Idle' },
  { id: HUB_TOOL_IDS.charactersContactDance, label: 'Characters — Contact Dance' },
  { id: HUB_TOOL_IDS.charactersSolo, label: 'Characters — Solo' },
  { id: HUB_TOOL_IDS.charactersSoloDance, label: 'Characters — Solo Dance' },
  { id: HUB_TOOL_IDS.eyesSakira, label: 'Characters — Eyes (Sakira)' },
  { id: HUB_TOOL_IDS.eyesSong, label: 'Characters — Eyes (Song)' },
  { id: HUB_TOOL_IDS.mediaLayout, label: 'Media Layout' },
  { id: HUB_TOOL_IDS.contactLayout, label: 'Contact Layout' },
  { id: HUB_TOOL_IDS.sceneLoadDebug, label: 'Scene Load Debug' },
  { id: HUB_TOOL_IDS.themeColors, label: 'Theme Colors' },
]

const SCENE_HUB_TOOL_IDS = new Set([
  HUB_TOOL_IDS.lights,
  HUB_TOOL_IDS.animation,
  HUB_TOOL_IDS.charactersDuoIdle,
  HUB_TOOL_IDS.charactersDuoDance,
  HUB_TOOL_IDS.charactersContactIdle,
  HUB_TOOL_IDS.charactersContactDance,
  HUB_TOOL_IDS.charactersSolo,
  HUB_TOOL_IDS.charactersSoloDance,
  HUB_TOOL_IDS.eyesSakira,
  HUB_TOOL_IDS.eyesSong,
  HUB_TOOL_IDS.mediaLayout,
  HUB_TOOL_IDS.contactLayout,
  HUB_TOOL_IDS.sceneLoadDebug,
])

const isSceneHubTool = (toolId) => SCENE_HUB_TOOL_IDS.has(toolId)

const LIGHTING_MENU_ITEMS = [
  item('Use Default Lighting', null, { actionKey: 'lightNormal' }),
  item('Use Flat Lighting', null, { actionKey: 'lightFlat' }),
  item('Use No Lights', null, { actionKey: 'lightOff' }),
]

const VIEWPORT_MENUS = [
  {
    id: VIEWPORT_MENU_IDS.view,
    label: 'View',
    disabled: true,
    items: [],
  },
  {
    id: VIEWPORT_MENU_IDS.shading,
    label: 'Shading',
    items: [
      item('Wireframe', null, { actionKey: 'wireframeOnly' }),
      item('Wireframe on Shaded', null, { actionKey: 'wireframeOnShaded' }),
      sep,
      item('Smooth Shade All', null, { actionKey: 'smooth' }),
      item('Hardware Texturing', null, { actionKey: 'textured' }),
    ],
  },
  {
    id: VIEWPORT_MENU_IDS.lighting,
    label: 'Lighting',
    items: LIGHTING_MENU_ITEMS,
  },
  {
    id: VIEWPORT_MENU_IDS.show,
    label: 'Show',
    items: [
      item('Show Controls', null, { toggleKey: 'showRig' }),
      item('Show Joints', null, { toggleKey: 'showJoints' }),
      item('Show Dialogue', null, { toggleKey: 'showDialogue' }),
    ],
  },
  {
    id: VIEWPORT_MENU_IDS.renderer,
    label: 'Renderer',
    disabled: true,
    items: [],
  },
  {
    id: VIEWPORT_MENU_IDS.panels,
    label: 'Panels',
    disabled: true,
    items: [],
  },
]

const MayaToolbarSvg = ({ children, viewBox = '0 0 16 16' }) => (
  <svg viewBox={viewBox} className="maya-viewport-svg" aria-hidden="true">
    {children}
  </svg>
)

const VIEWPORT_DECORATIVE_GROUPS = [
  {
    id: 'selection',
    tools: [
      { label: 'Select Tool', title: 'Select Tool', icon: <MayaToolbarSvg><path fill="currentColor" d="M2 2 12 8 7 9 6 14Z" /></MayaToolbarSvg> },
      { label: 'Lasso Select', title: 'Lasso Select', icon: <MayaToolbarSvg><path fill="none" stroke="currentColor" strokeWidth="1.2" d="M3 4c2-2 6-1 7 2s-1 6-4 6-4-2-4-4" /></MayaToolbarSvg> },
      { label: 'Move Tool', title: 'Move Tool', icon: <MayaToolbarSvg><path fill="currentColor" d="M8 2v12M2 8h12M8 5 5 8h6z" /></MayaToolbarSvg> },
      { label: 'Rotate Tool', title: 'Rotate Tool', icon: <MayaToolbarSvg><path fill="none" stroke="currentColor" strokeWidth="1.2" d="M8 3a5 5 0 1 1-3.5 8.5M8 3V1M8 3l2 2" /></MayaToolbarSvg> },
      { label: 'Scale Tool', title: 'Scale Tool', icon: <MayaToolbarSvg><path fill="none" stroke="currentColor" strokeWidth="1.2" d="M4 12 12 4M9 4h3v3M4 9v3h3" /></MayaToolbarSvg> },
    ],
  },
  {
    id: 'camera',
    tools: [
      { label: 'Look Through Selected', title: 'Look Through Selected', icon: <MayaToolbarSvg><rect x="2" y="4" width="12" height="8" fill="none" stroke="currentColor" strokeWidth="1.2" /><circle cx="8" cy="8" r="2" fill="currentColor" /></MayaToolbarSvg> },
      { label: 'Camera Bookmark', title: 'Camera Bookmark', icon: <MayaToolbarSvg><path fill="currentColor" d="M3 3h7v10H3zm8 2h2v8h-2z" /></MayaToolbarSvg> },
    ],
  },
  {
    id: 'display',
    tools: [
      { label: 'Grid', title: 'Grid', icon: <MayaViewportIcon src="/images/maya/grid.png" />, active: true },
      { label: 'Image Plane', title: 'Image Plane', icon: <MayaToolbarSvg><rect x="2" y="3" width="12" height="10" fill="none" stroke="currentColor" strokeWidth="1.2" /><path fill="currentColor" d="M2 10l3-3 2 2 3-4 4 5z" /></MayaToolbarSvg> },
    ],
  },
  {
    id: 'masks',
    tools: [
      { label: 'Selection Mask', title: 'Selection Mask', icon: <MayaToolbarSvg><rect x="3" y="3" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.2" strokeDasharray="2 1" /></MayaToolbarSvg> },
      { label: 'Object Mask', title: 'Object Mask', icon: <MayaToolbarSvg><circle cx="8" cy="8" r="4" fill="none" stroke="currentColor" strokeWidth="1.2" /></MayaToolbarSvg> },
      { label: 'Component Mask', title: 'Component Mask', icon: <MayaToolbarSvg><path fill="currentColor" d="M4 4h8v8H4z" opacity="0.35" /><circle cx="8" cy="8" r="1.5" fill="currentColor" /></MayaToolbarSvg> },
    ],
  },
  {
    id: 'render-overlays',
    tools: [
      { label: 'Shadows', title: 'Shadows', icon: <MayaToolbarSvg><circle cx="6" cy="6" r="3" fill="currentColor" /><ellipse cx="9" cy="11" rx="4" ry="1.5" fill="currentColor" opacity="0.45" /></MayaToolbarSvg> },
      { label: 'Ambient Occlusion', title: 'Ambient Occlusion', icon: <MayaToolbarSvg><circle cx="8" cy="8" r="5" fill="none" stroke="currentColor" strokeWidth="1.2" /><circle cx="8" cy="8" r="2.5" fill="currentColor" opacity="0.35" /></MayaToolbarSvg> },
      { label: 'Motion Blur', title: 'Motion Blur', icon: <MayaToolbarSvg><circle cx="6" cy="8" r="3" fill="currentColor" opacity="0.35" /><circle cx="10" cy="8" r="3" fill="currentColor" /></MayaToolbarSvg> },
      { label: 'Multisampling', title: 'Multisampling', icon: <MayaToolbarSvg><circle cx="8" cy="8" r="5" fill="none" stroke="currentColor" strokeWidth="1.2" /><path fill="none" stroke="currentColor" strokeWidth="1" d="M5 8h6M8 5v6" /></MayaToolbarSvg> },
    ],
  },
  {
    id: 'isolate',
    tools: [
      { label: 'Isolate Select', title: 'Isolate Select', icon: <MayaToolbarSvg><path fill="none" stroke="currentColor" strokeWidth="1.2" d="M3 5h10v6H3z" /><path fill="currentColor" d="M11 6l2-2v8l-2-2z" /></MayaToolbarSvg> },
    ],
  },
]

const MayaMenubar = ({ variant } = {}) => {
  const isFlat = variant === 'flat'
  const { pathname } = useLocation()
  const sceneName = sceneNameFromPath(pathname)
  const detailsProjectId = pathname.match(/^\/projects\/([^/]+)\/details\/?$/)?.[1] || null
  const isAboutRoute = pathname === '/about'
  const backEnabled = Boolean(detailsProjectId) || isAboutRoute
  const isHubPage =
    pathname === '/' ||
    pathname === '/contact' ||
    Boolean(pathname.match(/^\/projects\/[^/]+\/?$/))
  const wantedMeshes = useSceneWantedAssets()
  const wantedMeshesReady = useSceneWantedReady()
  const wantedRigIds = wantedMeshes
    .map((meshId) => MESH_TO_RIG_ASSET[meshId])
    .filter(Boolean)
  const wantedRigsReady = useAssetReady(wantedRigIds)
  const shadingLoading = !wantedMeshesReady.ready || wantedMeshesReady.loading
  const scaredReady = useAssetReady(SCARED_ASSET_IDS)
  const lightsOffLoading = !scaredReady.ready || scaredReady.loading
  const [openMenu, setOpenMenu] = useState(null)
  const [viewportBase, setViewportBase] = useState(VIEWPORT_SHADING_BASE.textured)
  const [viewportWireframeOnly, setViewportWireframeOnly] = useState(false)
  const [viewportWireframeOnShaded, setViewportWireframeOnShaded] = useState(false)
  const [lightingMode, setLightingMode] = useState(VIEWPORT_LIGHTING.normal)
  const [openWindowTools, setOpenWindowTools] = useState(() => new Set())
  const [showPolyCount, setShowPolyCount] = useState(true)
  const [showFps, setShowFps] = useState(true)
  const [showRig, setShowRig] = useState(() => readShowRigOverlay())
  const [showJoints, setShowJoints] = useState(() => readShowJointOverlay())
  const [showDialogue, setShowDialogue] = useState(() => readDialogueEnabled())
  const [isPassthroughRoute, setIsPassthroughRoute] = useState(
    () =>
      typeof document !== 'undefined'
      && document.body.classList.contains('hub-passthrough-route')
  )
  const [hudStats, setHudStats] = useState(EMPTY_VIEWPORT_HUD_STATS)
  const barRef = useRef(null)
  const fpsLabelRef = useRef(null)
  const closeMenuTimerRef = useRef(0)

  const cancelCloseMenus = () => {
    window.clearTimeout(closeMenuTimerRef.current)
  }

  const scheduleCloseMenus = () => {
    window.clearTimeout(closeMenuTimerRef.current)
    closeMenuTimerRef.current = window.setTimeout(() => {
      setOpenMenu(null)
    }, 80)
  }

  const hoverOpenMenu = (bar, id, disabled) => {
    if ((bar === 'main' && isFlat) || disabled || !openMenu) return
    setOpenMenu({ bar, id })
  }

  const emitShadingState = ({
    base = viewportBase,
    wireframeOnly = viewportWireframeOnly,
    wireframeOnShaded = viewportWireframeOnShaded,
    lightingMode: nextLightingMode = lightingMode,
  } = {}) => {
    emitViewportShading({
      base,
      wireframeOnly,
      wireframeOnShaded,
      lightingMode: nextLightingMode,
    })
  }

  const handleSetLightingMode = (mode) => {
    if (mode === VIEWPORT_LIGHTING.off && lightsOffLoading) return
    setLightingMode(mode)
    emitShadingState({ lightingMode: mode })
  }

  const handleToggleWireframeOnly = () => {
    if (shadingLoading) return
    const nextWireframeOnly = !viewportWireframeOnly
    setViewportWireframeOnly(nextWireframeOnly)
    if (nextWireframeOnly) setViewportWireframeOnShaded(false)
    emitShadingState({
      wireframeOnly: nextWireframeOnly,
      wireframeOnShaded: false,
    })
  }

  const handleToggleWireframeOnShaded = () => {
    if (shadingLoading) return
    if (viewportWireframeOnShaded) {
      setViewportWireframeOnShaded(false)
      emitShadingState({ wireframeOnShaded: false })
      return
    }

    const nextBase = viewportWireframeOnly
      ? VIEWPORT_SHADING_BASE.smooth
      : viewportBase
    setViewportWireframeOnly(false)
    setViewportWireframeOnShaded(true)
    if (viewportWireframeOnly) setViewportBase(VIEWPORT_SHADING_BASE.smooth)
    emitShadingState({
      base: nextBase,
      wireframeOnly: false,
      wireframeOnShaded: true,
    })
  }

  const handleSetViewportBase = (base) => {
    if (shadingLoading) return
    setViewportBase(base)
    if (viewportWireframeOnly) setViewportWireframeOnly(false)
    emitShadingState({
      base,
      wireframeOnly: false,
    })
  }

  const handleToggleHud = (hudKey) => {
    if (hudKey === 'poly') setShowPolyCount((current) => !current)
    if (hudKey === 'fps') setShowFps((current) => !current)
  }

  const showRigLoading =
    shadingLoading || (showRig && (!wantedRigsReady.ready || wantedRigsReady.loading))
  const showJointsLoading =
    shadingLoading || (showJoints && (!wantedRigsReady.ready || wantedRigsReady.loading))

  const handleToggleShowRig = () => {
    if (shadingLoading) return
    const anyOn = readShowRigOverlay()
    emitRigOverlay({ show: !anyOn })
  }

  const handleToggleShowJoints = () => {
    if (shadingLoading) return
    const anyOn = readShowJointOverlay()
    emitJointOverlay({ show: !anyOn })
  }

  const handleToggleShowDialogue = () => {
    setDialogueEnabled(!readDialogueEnabled())
  }

  useEffect(() => {
    const syncPassthroughRoute = () => {
      setIsPassthroughRoute(document.body.classList.contains('hub-passthrough-route'))
    }
    syncPassthroughRoute()
    const observer = new MutationObserver(syncPassthroughRoute)
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!isPassthroughRoute) return
    setOpenWindowTools((current) => {
      let changed = false
      const next = new Set(current)
      SCENE_HUB_TOOL_IDS.forEach((toolId) => {
        if (next.delete(toolId)) changed = true
      })
      return changed ? next : current
    })
  }, [isPassthroughRoute])

  useEffect(() => {
    const handleRigOverlay = (event) => {
      setShowRig(Boolean(event.detail?.show))
    }
    const handleJointOverlay = (event) => {
      setShowJoints(Boolean(event.detail?.show))
    }
    const handleDialogueToggle = (event) => {
      setShowDialogue(event.detail?.enabled !== false)
    }
    window.addEventListener(RIG_OVERLAY_EVENT, handleRigOverlay)
    window.addEventListener(JOINT_OVERLAY_EVENT, handleJointOverlay)
    window.addEventListener(DIALOGUE_TOGGLE_EVENT, handleDialogueToggle)
    return () => {
      window.removeEventListener(RIG_OVERLAY_EVENT, handleRigOverlay)
      window.removeEventListener(JOINT_OVERLAY_EVENT, handleJointOverlay)
      window.removeEventListener(DIALOGUE_TOGGLE_EVENT, handleDialogueToggle)
    }
  }, [])

  useEffect(() => {
    const handleHudStats = (event) => {
      setHudStats({ ...EMPTY_VIEWPORT_HUD_STATS, ...(event.detail || {}) })
    }
    window.addEventListener(VIEWPORT_HUD_STATS_EVENT, handleHudStats)
    return () => window.removeEventListener(VIEWPORT_HUD_STATS_EVENT, handleHudStats)
  }, [])

  useEffect(() => {
    const handleViewportShading = (event) => {
      const detail = event.detail || {}
      if (detail.base === VIEWPORT_SHADING_BASE.smooth || detail.base === VIEWPORT_SHADING_BASE.textured) {
        setViewportBase(detail.base)
      }
      if (typeof detail.wireframeOnly === 'boolean') setViewportWireframeOnly(detail.wireframeOnly)
      if (typeof detail.wireframeOnShaded === 'boolean') {
        setViewportWireframeOnShaded(detail.wireframeOnShaded)
      }
      if (detail.lightingMode) setLightingMode(detail.lightingMode)
    }
    window.addEventListener(VIEWPORT_SHADING_EVENT, handleViewportShading)
    return () => window.removeEventListener(VIEWPORT_SHADING_EVENT, handleViewportShading)
  }, [])

  useEffect(() => {
    if (!showFps || !isHubPage) return undefined
    let frames = 0
    let last = performance.now()
    let raf = 0
    const tick = (now) => {
      frames += 1
      if (now - last >= 400) {
        const label = fpsLabelRef.current
        if (label) {
          label.textContent = `${Math.round((frames * 1000) / (now - last))} fps`
        }
        frames = 0
        last = now
      }
      raf = window.requestAnimationFrame(tick)
    }
    raf = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(raf)
  }, [showFps, isHubPage])

  const handleViewportMenuAction = (actionKey) => {
    if (actionKey === 'wireframeOnly') handleToggleWireframeOnly()
    if (actionKey === 'wireframeOnShaded') handleToggleWireframeOnShaded()
    if (actionKey === 'smooth') handleSetViewportBase(VIEWPORT_SHADING_BASE.smooth)
    if (actionKey === 'textured') handleSetViewportBase(VIEWPORT_SHADING_BASE.textured)
    if (actionKey === 'lightNormal') handleSetLightingMode(VIEWPORT_LIGHTING.normal)
    if (actionKey === 'lightFlat') handleSetLightingMode(VIEWPORT_LIGHTING.flat)
    if (actionKey === 'lightOff') handleSetLightingMode(VIEWPORT_LIGHTING.off)
  }

  const handleToggleViewportMenu = (id) => {
    const target = VIEWPORT_MENUS.find((menu) => menu.id === id)
    if (target?.disabled) return
    setOpenMenu((current) => (
      current?.bar === 'viewport' && current.id === id ? null : { bar: 'viewport', id }
    ))
  }

  const handleSelectViewportItem = (entry) => {
    if (entry.disabled) return
    if (entry.toggleKey === 'showRig') {
      handleToggleShowRig()
      return
    }
    if (entry.toggleKey === 'showJoints') {
      handleToggleShowJoints()
      return
    }
    if (entry.toggleKey === 'showDialogue') {
      handleToggleShowDialogue()
      return
    }
    if (entry.actionKey) handleViewportMenuAction(entry.actionKey)
  }

  const renderViewportMenuItems = (menuId, items) =>
    items.map((entry, index) => {
      if (entry.separator) {
        return <div key={`sep-${menuId}-${index}`} className="maya-menu-sep" role="separator" />
      }
      const hasDot = Boolean(entry.toggleKey || entry.actionKey)
      const isToggleItem =
        entry.toggleKey === 'showRig'
        || entry.toggleKey === 'showJoints'
        || entry.toggleKey === 'showDialogue'
      const isShadingAction = Boolean(
        entry.actionKey
        && (
          entry.actionKey === 'wireframeOnly'
          || entry.actionKey === 'wireframeOnShaded'
          || entry.actionKey === 'smooth'
          || entry.actionKey === 'textured'
        )
      )
      const itemLoading =
        (isShadingAction && shadingLoading)
        || (entry.actionKey === 'lightOff' && lightsOffLoading)
        || (entry.toggleKey === 'showRig' && showRigLoading)
        || (entry.toggleKey === 'showJoints' && showJointsLoading)
      const isActive =
        (entry.actionKey === 'wireframeOnly' && viewportWireframeOnly)
        || (entry.actionKey === 'wireframeOnShaded' && viewportWireframeOnShaded)
        || (
          entry.actionKey === 'smooth'
          && viewportBase === VIEWPORT_SHADING_BASE.smooth
          && !viewportWireframeOnly
        )
        || (
          entry.actionKey === 'textured'
          && viewportBase === VIEWPORT_SHADING_BASE.textured
          && !viewportWireframeOnly
        )
        || (entry.actionKey === 'lightNormal' && lightingMode === VIEWPORT_LIGHTING.normal)
        || (entry.actionKey === 'lightFlat' && lightingMode === VIEWPORT_LIGHTING.flat)
        || (entry.actionKey === 'lightOff' && lightingMode === VIEWPORT_LIGHTING.off)
        || (entry.toggleKey === 'showRig' && showRig)
        || (entry.toggleKey === 'showJoints' && showJoints)
        || (entry.toggleKey === 'showDialogue' && showDialogue)
      return (
        <button
          key={`${menuId}-${entry.label}`}
          type="button"
          role={hasDot ? (isToggleItem ? 'menuitemcheckbox' : 'menuitemradio') : 'menuitem'}
          aria-checked={hasDot ? isActive : undefined}
          disabled={Boolean(entry.disabled)}
          className="maya-menu-item"
          {...loadingAttrs(itemLoading)}
          onClick={() => {
            if (itemLoading) return
            handleSelectViewportItem(entry)
          }}
        >
          <MenuDot filled={isActive} reserved={!hasDot} />
          <span>{entry.label}</span>
          {entry.shortcut || entry.hint ? (
            <span className="maya-menu-shortcut">{entry.shortcut || entry.hint}</span>
          ) : null}
        </button>
      )
    })

  const handleOpenScene = (path) => {
    requestNavigation(path, { explicit: true })
  }

  const handleNewScene = () => {
    startNewHubScene()
  }

  const handleBack = () => {
    if (detailsProjectId) {
      requestNavigation(getProjectPath(detailsProjectId), { explicit: true })
      return
    }
    if (isAboutRoute) {
      requestNavigation('/', { explicit: true })
    }
  }

  const isSceneToolAvailable = !isPassthroughRoute
  const isHubToolEntryAvailable = (toolId) =>
    !toolId || !isSceneHubTool(toolId) || isSceneToolAvailable

  const availableWindowTools = WINDOW_TOOLS.filter((tool) => {
    if (!isHubToolEntryAvailable(tool.id)) return false
    if (OWNER_WINDOW_TOOL_IDS.has(tool.id) && !IS_OWNER) return false
    return true
  })

  const allToolsOpen =
    availableWindowTools.length > 0
    && availableWindowTools.every((tool) => openWindowTools.has(tool.id))

  const handleToggleAllTools = () => {
    if (allToolsOpen) {
      availableWindowTools.forEach((tool) => emitHubTool(tool.id, 'close'))
      setOpenWindowTools((current) => {
        const next = new Set(current)
        availableWindowTools.forEach((tool) => next.delete(tool.id))
        return next
      })
      return
    }
    availableWindowTools.forEach((tool) => {
      if (openWindowTools.has(tool.id)) return
      emitHubTool(tool.id, 'open')
    })
    setOpenWindowTools((current) => {
      const next = new Set(current)
      availableWindowTools.forEach((tool) => next.add(tool.id))
      return next
    })
  }

  const fileOpenItems = [
    item('About', () => handleOpenScene('/about')),
    ...featuredProjects.map((project) =>
      item(`Explore ${project.title}`, () => handleOpenScene(getProjectDetailsPath(project.id)))
    ),
    sep,
    item('Open Showreel', () => handleOpenScene('/')),
    item('Open Contact', () => handleOpenScene('/contact')),
    ...featuredProjects.map((project) =>
      item(`Open ${project.title}`, () => handleOpenScene(getProjectPath(project.id)))
    ),
  ]

  const menus = [
    {
      id: 'file',
      label: 'File',
      items: isFlat
        ? fileOpenItems
        : [
            item('New Scene', () => handleNewScene(), { shortcut: 'Ctrl+N' }),
            sep,
            ...fileOpenItems,
            sep,
            item('Save Tool Settings', () => emitHubTool('all', 'save'), { shortcut: 'Ctrl+S' }),
            item('Reset Tool Settings', () => emitHubTool('all', 'reset')),
          ],
    },
    {
      id: 'edit',
      label: 'Edit',
      items: [
        item('Reset Lights', () => emitHubTool(HUB_TOOL_IDS.lights, 'reset'), { hubToolId: HUB_TOOL_IDS.lights }),
        item('Reset Theme Colors', () => emitHubTool(HUB_TOOL_IDS.themeColors, 'reset')),
        sep,
        item('Copy Lights', () => emitHubTool(HUB_TOOL_IDS.lights, 'copy'), { hubToolId: HUB_TOOL_IDS.lights }),
        item('Copy Theme Colors', () => emitHubTool(HUB_TOOL_IDS.themeColors, 'copy')),
      ],
    },
    {
      id: 'create',
      label: 'Create',
      disabled: true,
      items: [],
    },
    {
      id: 'select',
      label: 'Select',
      disabled: true,
      items: [],
    },
    {
      id: 'display',
      label: 'Display',
      items: [
        item('Poly Count', null, { hudKey: 'poly' }),
        item('Frame Rate', null, { hudKey: 'fps' }),
      ],
    },
    {
      id: 'windows',
      label: 'Windows',
      items: [
        { label: 'Open All Tools', allToolsKey: true },
        sep,
        ...availableWindowTools.map((tool) => ({
          label: tool.label,
          toolId: tool.id,
        })),
      ],
    },
    {
      id: 'mesh',
      label: 'Mesh',
      disabled: true,
      items: [],
    },
    {
      id: 'mesh-display',
      label: 'Mesh Display',
      disabled: true,
      items: [],
    },
  ]
    .filter((menu) => !(isFlat && menu.disabled))
    .map((menu) => (isFlat && menu.id !== 'file' ? { ...menu, disabled: true } : menu))

  const handleToggleWindowTool = (toolId) => {
    if (!isHubToolEntryAvailable(toolId)) return
    const isOn = openWindowTools.has(toolId)
    setOpenWindowTools((current) => {
      const next = new Set(current)
      if (isOn) next.delete(toolId)
      else next.add(toolId)
      return next
    })
    emitHubTool(toolId, isOn ? 'close' : 'open')
  }

  const handleToggleMenu = (id) => {
    const target = menus.find((menu) => menu.id === id)
    if (target?.disabled) return
    setOpenMenu((current) => (
      current?.bar === 'main' && current.id === id ? null : { bar: 'main', id }
    ))
  }

  const handleSelectItem = (entry) => {
    if (entry.disabled || !entry.action) return
    entry.action()
    setOpenMenu(null)
  }

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!barRef.current?.contains(event.target)) {
        setOpenMenu(null)
      }
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpenMenu(null)
        return
      }
      if (isFlat) return
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'n') {
        event.preventDefault()
        handleNewScene()
        return
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault()
        emitHubTool('all', 'save')
      }
    }
    document.addEventListener(isFlat ? 'pointerdown' : 'mousedown', handlePointerDown)
    window.addEventListener('keydown', handleKeyDown)
    const handleHubTool = (event) => {
      const { id, action } = event.detail || {}
      if (!id || id === 'all') return
      if (!isHubToolEntryAvailable(id)) return
      if (action === 'open' || action === 'openHidden') {
        setOpenWindowTools((current) => {
          if (current.has(id)) return current
          const next = new Set(current)
          next.add(id)
          return next
        })
      }
      if (action === 'close') {
        setOpenWindowTools((current) => {
          if (!current.has(id)) return current
          const next = new Set(current)
          next.delete(id)
          return next
        })
      }
    }
    window.addEventListener(HUB_TOOL_EVENT, handleHubTool)
    return () => {
      window.clearTimeout(closeMenuTimerRef.current)
      document.removeEventListener(isFlat ? 'pointerdown' : 'mousedown', handlePointerDown)
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener(HUB_TOOL_EVENT, handleHubTool)
    }
  }, [isFlat])

  return (
    <div
      ref={barRef}
      className={isFlat ? 'maya-chrome maya-chrome--flat' : 'maya-chrome'}
      role="banner"
    >
      <div
        className={isFlat ? undefined : 'site-intro-menubar site-intro-item'}
        style={isFlat ? undefined : { '--intro-delay': '0ms' }}
      >
      {isFlat ? null : (
        <div className="maya-titlebar">
          <MayaLogo />
          <p className="maya-titlebar-label">
            {sceneName}* - Autotable Daya 2026: {sceneName}
          </p>
        </div>
      )}
      <div
        className="maya-menubar"
        role="menubar"
        aria-label="Maya menus"
        onMouseEnter={isFlat ? undefined : cancelCloseMenus}
        onMouseLeave={isFlat ? undefined : scheduleCloseMenus}
      >
        <button
          type="button"
          className="maya-home"
          aria-label="Go to showreel"
          onClick={() => handleOpenScene('/')}
        >
          <HomeIcon />
        </button>
        {menus.map((menu) => {
          const isOpen = !menu.disabled && openMenu?.bar === 'main' && openMenu.id === menu.id
          return (
            <div
              key={menu.id}
              className={menu.disabled ? 'maya-menu maya-menu-decor' : 'maya-menu'}
            >
              <button
                type="button"
                className={
                  menu.disabled
                    ? 'maya-menu-trigger maya-menu-decor'
                    : isOpen
                      ? 'maya-menu-trigger is-open'
                      : 'maya-menu-trigger'
                }
                role="menuitem"
                aria-haspopup="true"
                aria-expanded={isOpen}
                disabled={Boolean(menu.disabled)}
                onClick={() => handleToggleMenu(menu.id)}
                onMouseEnter={() => hoverOpenMenu('main', menu.id, menu.disabled)}
              >
                {menu.label}
              </button>
              {isOpen ? (
                <div className="maya-menu-panel" role="menu" aria-label={menu.label}>
                  {menu.items.map((entry, index) => {
                    if (entry.separator) {
                      return <div key={`sep-${index}`} className="maya-menu-sep" role="separator" />
                    }
                    const isAllTools = Boolean(entry.allToolsKey)
                    const isWindowTool = Boolean(entry.toolId)
                    const isHudItem = Boolean(entry.hudKey)
                    const hubToolUnavailable =
                      Boolean(entry.hubToolId) && !isHubToolEntryAvailable(entry.hubToolId)
                    const windowToolUnavailable =
                      isWindowTool && !isHubToolEntryAvailable(entry.toolId)
                    const isOn = isAllTools
                      ? allToolsOpen
                      : isWindowTool
                        ? openWindowTools.has(entry.toolId) && isHubToolEntryAvailable(entry.toolId)
                        : entry.hudKey === 'poly'
                          ? showPolyCount
                          : entry.hudKey === 'fps'
                            ? showFps
                            : false
                    const isEntryDisabled =
                      Boolean(entry.disabled) || hubToolUnavailable || windowToolUnavailable
                    const hasDot = isAllTools || isWindowTool || isHudItem
                    return (
                      <button
                        key={`${menu.id}-${entry.label}`}
                        type="button"
                        role={
                          hasDot
                            ? 'menuitemcheckbox'
                            : 'menuitem'
                        }
                        aria-checked={
                          hasDot ? isOn : undefined
                        }
                        disabled={isEntryDisabled}
                        className="maya-menu-item"
                        onClick={() => {
                          if (isEntryDisabled) return
                          if (isAllTools) {
                            handleToggleAllTools()
                            return
                          }
                          if (isWindowTool) {
                            handleToggleWindowTool(entry.toolId)
                            return
                          }
                          if (isHudItem) {
                            handleToggleHud(entry.hudKey)
                            return
                          }
                          handleSelectItem(entry)
                        }}
                      >
                        <MenuDot filled={isOn} reserved={!hasDot} />
                        <span>{entry.label}</span>
                        {entry.shortcut || entry.hint ? (
                          <span className="maya-menu-shortcut">{entry.shortcut || entry.hint}</span>
                        ) : null}
                      </button>
                    )
                  })}
                </div>
              ) : null}
            </div>
          )
        })}
        <div className="maya-status-tools" role="toolbar" aria-label="Scene tools">
          <MayaQuickStatus
            backEnabled={backEnabled}
            onNewScene={handleNewScene}
            onBack={handleBack}
            variant={variant}
          />
        </div>
      </div>
      {isFlat ? null : (
      <>
      <div className="maya-viewport-toolbar" aria-label="Viewport toolbar">
        <div
          className="maya-viewport-menubar"
          role="menubar"
          aria-label="Viewport menus"
          onMouseEnter={cancelCloseMenus}
          onMouseLeave={scheduleCloseMenus}
        >
          {VIEWPORT_MENUS.map((menu) => {
            const isOpen = !menu.disabled && openMenu?.bar === 'viewport' && openMenu.id === menu.id
            return (
              <div
                key={menu.id}
                className={menu.disabled ? 'maya-menu maya-menu-decor' : 'maya-menu'}
              >
                <button
                  type="button"
                  className={
                    menu.disabled
                      ? 'maya-menu-trigger maya-menu-decor'
                      : isOpen
                        ? 'maya-menu-trigger is-open'
                        : 'maya-menu-trigger'
                  }
                  role="menuitem"
                  aria-haspopup="true"
                  aria-expanded={isOpen}
                  disabled={Boolean(menu.disabled)}
                  onClick={() => handleToggleViewportMenu(menu.id)}
                  onMouseEnter={() => hoverOpenMenu('viewport', menu.id, menu.disabled)}
                >
                  {menu.label}
                </button>
                {isOpen ? (
                  <div className="maya-menu-panel" role="menu" aria-label={menu.label}>
                    {renderViewportMenuItems(menu.id, menu.items)}
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
        <div className="maya-viewport-iconbar" role="toolbar" aria-label="Viewport tools">
          {VIEWPORT_DECORATIVE_GROUPS.slice(0, 2).map((group, index) => (
            <div key={group.id} className="maya-viewport-group-wrap maya-viewport-iconbar-decor">
              {index > 0 ? <span className="maya-viewport-divider" aria-hidden="true" /> : null}
              <div className="maya-viewport-group">
                {group.tools.map(({ label, title, icon, active }) => (
                  <DecorControl
                    key={`${group.id}-${label}`}
                    className={active ? 'maya-viewport-btn is-active' : 'maya-viewport-btn'}
                    label={label}
                    title={title}
                  >
                    {icon}
                  </DecorControl>
                ))}
              </div>
            </div>
          ))}
          <span className="maya-viewport-divider maya-viewport-iconbar-decor" aria-hidden="true" />
          <div className="maya-viewport-group" aria-label="Viewport display">
            <DecorControl className="maya-viewport-btn is-active" label="Grid">
              <MayaViewportIcon src="/images/maya/grid.png" />
            </DecorControl>
            <DecorControl
              className="maya-viewport-btn maya-viewport-iconbar-decor"
              label="Image Plane"
            >
              <MayaToolbarSvg>
                <rect x="2" y="3" width="12" height="10" fill="none" stroke="currentColor" strokeWidth="1.2" />
                <path fill="currentColor" d="M2 10l3-3 2 2 3-4 4 5z" />
              </MayaToolbarSvg>
            </DecorControl>
          </div>
          <span className="maya-viewport-divider" aria-hidden="true" />
          <div className="maya-viewport-group" aria-label="Viewport shading">
            <button
              type="button"
              className={viewportWireframeOnly ? 'maya-viewport-btn is-active' : 'maya-viewport-btn'}
              aria-label="Wireframe"
              title="Wireframe"
              aria-pressed={viewportWireframeOnly}
              {...loadingAttrs(shadingLoading)}
              onClick={handleToggleWireframeOnly}
            >
              <MayaViewportIcon src="/images/maya/wireframe-only.png" />
            </button>
            <button
              type="button"
              className={viewportWireframeOnShaded ? 'maya-viewport-btn is-active' : 'maya-viewport-btn'}
              aria-label="Wireframe on Shaded"
              title="Wireframe on Shaded"
              aria-pressed={viewportWireframeOnShaded}
              {...loadingAttrs(shadingLoading)}
              onClick={handleToggleWireframeOnShaded}
            >
              <MayaViewportIcon src="/images/maya/wireframe-on-shaded.png" />
            </button>
            {VIEWPORT_BASE_TOOLS.map(({ id, label, title, src }) => {
              const isActive = !viewportWireframeOnly && viewportBase === id
              return (
                <button
                  key={id}
                  type="button"
                  className={isActive ? 'maya-viewport-btn is-active' : 'maya-viewport-btn'}
                  aria-label={label}
                  title={title}
                  aria-pressed={isActive}
                  {...loadingAttrs(shadingLoading)}
                  onClick={() => handleSetViewportBase(id)}
                >
                  <MayaViewportIcon src={src} />
                </button>
              )
            })}
            <button
              type="button"
              className={
                lightingMode === VIEWPORT_LIGHTING.off
                  ? 'maya-viewport-btn is-active'
                  : 'maya-viewport-btn'
              }
              aria-label="Use No Lights"
              title="Use No Lights"
              aria-pressed={lightingMode === VIEWPORT_LIGHTING.off}
              {...loadingAttrs(lightsOffLoading)}
              onClick={() =>
                handleSetLightingMode(
                  lightingMode === VIEWPORT_LIGHTING.off
                    ? VIEWPORT_LIGHTING.normal
                    : VIEWPORT_LIGHTING.off
                )
              }
            >
              <MayaViewportIcon src="/images/maya/lights.png" />
            </button>
          </div>
          <span className="maya-viewport-divider" aria-hidden="true" />
          {VIEWPORT_DECORATIVE_GROUPS.slice(3).map((group, index) => (
            <div key={group.id} className="maya-viewport-group-wrap maya-viewport-iconbar-decor">
              {index > 0 ? <span className="maya-viewport-divider" aria-hidden="true" /> : null}
              <div className="maya-viewport-group">
                {group.tools.map(({ label, title, icon, active }) => (
                  <DecorControl
                    key={`${group.id}-${label}`}
                    className={active ? 'maya-viewport-btn is-active' : 'maya-viewport-btn'}
                    label={label}
                    title={title}
                  >
                    {icon}
                  </DecorControl>
                ))}
              </div>
            </div>
          ))}
          <span className="maya-viewport-divider maya-viewport-iconbar-decor" aria-hidden="true" />
          <div className="maya-viewport-fields maya-viewport-iconbar-decor" aria-label="Viewport display settings">
            <span className="maya-viewport-field" aria-hidden="true">0.00</span>
            <span className="maya-viewport-field" aria-hidden="true">1.00</span>
            <DecorControl className="maya-viewport-btn" label="Audio">
              <MayaToolbarSvg viewBox="0 0 16 16">
                <path fill="currentColor" d="M3 6h2l3-3v10l-3-3H3z" />
                <path fill="none" stroke="currentColor" strokeWidth="1.2" d="M11 6a2 2 0 0 1 0 4" />
              </MayaToolbarSvg>
            </DecorControl>
            <span className="maya-viewport-on" aria-hidden="true">ON</span>
            <DecorControl className="maya-viewport-aces" label="sRGB">
              sRGB
            </DecorControl>
          </div>
        </div>
      </div>
      {isHubPage && (showFps || showPolyCount)
        ? createPortal(
          <div className="maya-viewport-hud" aria-hidden="true">
            {showFps ? <p ref={fpsLabelRef} className="maya-viewport-hud-fps">0 fps</p> : null}
            {showPolyCount ? (
              <div className="maya-viewport-hud-table">
                {[
                  ['Verts', hudStats.verts],
                  ['Edges', hudStats.edges],
                  ['Faces', hudStats.faces],
                  ['Tris', hudStats.tris],
                ].map(([label, value]) => (
                  <div key={label} className="maya-viewport-hud-row">
                    <span>{label}:</span>
                    <span>{value}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>,
          document.body
        )
        : null}
      </>
      )}
      </div>
    </div>
  )
}

export default MayaMenubar
